import { AfterViewInit, Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { debounceTime, filter, take } from 'rxjs';

import { NbIconLibraries } from '@nebular/theme';
import { Store } from '@ngrx/store';
import { fadeInOnEnterAnimation, fadeOutOnLeaveAnimation } from 'angular-animations';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Toast } from 'primeng/toast';

import { selectInitialAuthAttempt } from '@app/auth/store/selectors/user.selectors';
import { CommonLoadingComponent } from '@app/common-modules/common-loading/components/common-loading/common-loading.component';
import { CommonPwaInstallService } from '@app/common-modules/common-pwa/services/common-pwa-install.service';
import { CommonPwaUpdateService } from '@app/common-modules/common-pwa/services/common-pwa-update.service';
import { OpenaiChatkitService } from '@app/libraries/openai-chatkit/openai-chatkit.service';
import { PosthogService } from '@app/services/integrations/posthog.service';
import { SlnkGoogleTagManagerService } from '@app/services/integrations/slnk-google-tag-manager.service';
import { SupportChatService } from '@app/services/integrations/support-chat.service';
import { UserflowService } from '@app/services/integrations/userflow.service';
import { selectIsNavigationLoadingModules } from '@app/store/selectors/navigation.selectors';

// oxlint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- default CD required so zoned updates still reach descendants
@Component({
  selector: 'slnk-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [fadeInOnEnterAnimation({ delay: 0 }), fadeOutOnLeaveAnimation()],
  imports: [RouterOutlet, CommonLoadingComponent, Toast, ConfirmDialog],
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router);
  private readonly iconLibraries = inject(NbIconLibraries);
  private readonly appUpdateService = inject(CommonPwaUpdateService);
  private readonly appInstallService = inject(CommonPwaInstallService);
  private readonly supportChatService = inject(SupportChatService);
  private readonly userFlowService = inject(UserflowService);
  private readonly posthogService = inject(PosthogService);
  private readonly slnkGtmService = inject(SlnkGoogleTagManagerService);
  private readonly openaiChatkitSvc = inject(OpenaiChatkitService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);

  private readonly isLoadingModules = toSignal(this.store.select(selectIsNavigationLoadingModules));
  private readonly initialAuthAttempt = toSignal(this.store.select(selectInitialAuthAttempt));

  public readonly showLoadingIndicator = computed(() => {
    const isLoadingModules = this.isLoadingModules();
    const initialAuthAttempt = this.initialAuthAttempt();
    const toBeAuthenticated = !initialAuthAttempt;

    return isLoadingModules || toBeAuthenticated;
  });

  public ngOnInit(): void {
    this.posthogService.init();
    this.iconLibraries.registerFontPack('solid', { packClass: 'fas', iconClassPrefix: 'fa' });
    this.iconLibraries.registerFontPack('regular', { packClass: 'far', iconClassPrefix: 'fa' });
    this.iconLibraries.registerFontPack('brands', { packClass: 'fab', iconClassPrefix: 'fa' });
    this.iconLibraries.setDefaultPack('regular');
    this.supportChatService.addHideCrispClass();
    this.userFlowService.userFlowInit();
    this.openaiChatkitSvc.initChatkit();
    this.globalLazyLoadingIndicator();
  }

  public ngAfterViewInit(): void {
    this.firstAppLoadNavigationEnd();
  }

  /**
   * Check if app is loading new module
   */
  public globalLazyLoadingIndicator(): void {
    // The Router emits special events for "loadChildren" configuration loading. We
    // just need to listen for the Start and End events in order to determine if we
    // have any pending configuration requests.
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (event): void => {
        // PUSH GTM ROUTER CHANGES
        if (event instanceof NavigationEnd) {
          this.slnkGtmService.routerNavigationTracking(event);
          this.supportChatService.trackRouteChance(event.url);
        }
      },
    });
  }

  public firstAppLoadNavigationEnd(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        debounceTime(400),
        take(1),
      )
      .subscribe(() => {
        this.appInstallService.initAppInstallPrompt();
        this.appUpdateService.initAppUpdateCheck();
      });
  }
}
